import { create } from 'zustand';
import { validateFileSize } from '@/lib/upload';

export interface PartnersSectionImage {
  url: string;
  formats?: {
    thumbnail?: { url: string };
    small?: { url: string };
  };
}

export interface PartnersSection {
  id: number;
  documentId: string;
  strategicImage?: PartnersSectionImage | null;
  alignedImage?: PartnersSectionImage | null;
  awarenessImage?: PartnersSectionImage | null;
}

export type PartnerTier = 'strategic' | 'aligned' | 'awareness';

export const PARTNER_TIER_FIELD: Record<PartnerTier, keyof PartnersSection> = {
  strategic: 'strategicImage',
  aligned: 'alignedImage',
  awareness: 'awarenessImage',
};

interface PartnersSectionStore {
  partnersSection: PartnersSection | null;
  loading: boolean;
  saving: boolean;
  fetchPartnersSection: () => Promise<void>;
  /**
   * undefined = keep existing | null = clear field | File = upload new
   */
  updatePartnersSection: (data: {
    strategicImage?: File | null;
    alignedImage?: File | null;
    awarenessImage?: File | null;
  }) => Promise<PartnersSection | null>;
}

const baseUrl = '/api/strapi';

async function uploadImage(file: File): Promise<number> {
  validateFileSize(file);
  const formData = new FormData();
  formData.append('files', file);
  const res = await fetch(`${baseUrl}/upload`, { method: 'POST', body: formData });
  if (!res.ok) {
    // Surface the real cause so the UI can show it. Strapi returns JSON
    // {error:{message}} for app-layer errors; WAF/ALB return text or empty.
    const bodyText = await res.text().catch(() => '');
    let detail = '';
    if (bodyText) {
      try {
        const parsed = JSON.parse(bodyText) as { error?: { message?: string }; message?: string };
        detail = parsed?.error?.message ?? parsed?.message ?? '';
      } catch (parseErr) {
        console.debug('[partnersSectionStore] non-JSON error body', parseErr);
        detail = bodyText.slice(0, 200);
      }
    }
    throw new Error(
      `Upload failed: ${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`,
    );
  }
  const files = await res.json();
  const id = files[0]?.id;
  if (typeof id !== 'number') {
    throw new Error('Upload succeeded but server did not return a file id');
  }
  return id;
}

export const usePartnersSectionStore = create<PartnersSectionStore>((set) => ({
  partnersSection: null,
  loading: false,
  saving: false,

  fetchPartnersSection: async () => {
    set({ loading: true });
    try {
      const res = await fetch(`${baseUrl}/partners-section?populate=*`);
      if (res.ok) {
        const data = await res.json();
        set({ partnersSection: data.data ?? null });
      }
    } catch (err) {
      console.error('Error fetching partners section:', err);
    } finally {
      set({ loading: false });
    }
  },

  updatePartnersSection: async ({ strategicImage, alignedImage, awarenessImage }) => {
    set({ saving: true });
    try {
      const payload: Record<string, unknown> = {};

      const apply = async (
        key: 'strategicImage' | 'alignedImage' | 'awarenessImage',
        value: File | null | undefined,
      ) => {
        if (value instanceof File) {
          payload[key] = await uploadImage(value);
        } else if (value === null) {
          payload[key] = null;
        }
      };

      await apply('strategicImage', strategicImage);
      await apply('alignedImage', alignedImage);
      await apply('awarenessImage', awarenessImage);

      // Strapi v5's single-type PUT returns the updated row with relations as
      // bare ids unless `populate` is specified. Without populate, our local
      // state ends up with `<field>: { id }` (no url) -- the preview disappears
      // until the user refreshes the page. Always populate on update.
      const res = await fetch(`${baseUrl}/partners-section?populate=*`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        let detail = '';
        if (bodyText) {
          try {
            const parsed = JSON.parse(bodyText) as {
              error?: { message?: string };
              message?: string;
            };
            detail = parsed?.error?.message ?? parsed?.message ?? '';
          } catch (parseErr) {
            console.debug('[partnersSectionStore] non-JSON error body', parseErr);
            detail = bodyText.slice(0, 200);
          }
        }
        throw new Error(
          `Save failed: ${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`,
        );
      }
      const result = await res.json();
      set({ partnersSection: result.data ?? null });
      return result.data ?? null;
    } finally {
      set({ saving: false });
    }
  },
}));
